/**
 * Recovers from running an old build after a deploy has replaced it.
 *
 * Every deploy removes the previous build, and the browser can still hold an
 * old copy of the app: it reuses a cached page for Back and Forward even when
 * that copy is stale, and a tab may simply have been left open. That old copy
 * then asks for a page's code by a file name that no longer exists, the server
 * answers with the app's HTML instead, and the navigation fails — a blank white
 * screen with "Loading chunk … failed" in the console.
 *
 * The cure is the one a person would reach for: reload. A reload revalidates
 * the page with the server, so it comes back as the current build, whose files
 * all exist. Done once, automatically, at the moment the failure happens.
 */
import { inject, Injectable } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/** Remembers the last automatic reload, so a server that is truly broken cannot loop. */
const LAST_RELOAD_KEY = 'sso_stale_build_reload_at';
const RELOAD_GUARD_MS = 10_000;

/** What webpack, and browsers loading a module directly, say when a page's code is missing. */
export function isMissingCodeError(error: unknown): boolean {
  const err = error as { name?: unknown; message?: unknown } | null;
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const message = typeof err.message === 'string' ? err.message : '';
  return (
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

@Injectable({ providedIn: 'root' })
export class StaleBuildRecoveryService {
  private readonly router = inject(Router);

  private started = false;

  /** Idempotent. Registered as an app initializer, so it is listening before the first navigation. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => {
        if (isMissingCodeError(event.error)) this.reloadInto(event.url);
      });
  }

  /**
   * Load `url` from the server again.
   *
   * A plain reload rather than navigating to the address: navigating may be
   * answered from the same stale cache, while a reload always revalidates the
   * page. The router has already put the previous address back by now, so the
   * one that failed is put in place first and the reload lands where the person
   * was going — in place of the entry they arrived on when this is the first
   * page the app opened (Back, a reload, a link), and as a new entry when they
   * were moving between pages.
   */
  private reloadInto(url: string): void {
    if (this.reloadedRecently()) return;
    try {
      if (url && url !== window.location.pathname + window.location.search + window.location.hash) {
        if (this.router.navigated) {
          window.history.pushState(null, '', url);
        } else {
          window.history.replaceState(null, '', url);
        }
      }
    } catch {
      // An address the browser refuses to put in place — reload where we are.
    }
    this.reload();
  }

  /** Its own method so a test can observe it — jsdom will not let one replace `location.reload`. */
  protected reload(): void {
    window.location.reload();
  }

  /**
   * Records this attempt and says whether one was made moments ago. Storage
   * can be unavailable (private windows, blocked site data); without it there
   * is no guard, but there is also no loop, since the reload starts afresh.
   */
  private reloadedRecently(): boolean {
    try {
      const last = Number(sessionStorage.getItem(LAST_RELOAD_KEY) ?? 0);
      if (Date.now() - last < RELOAD_GUARD_MS) return true;
      sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
    } catch {
      // Unavailable storage: reload without a guard.
    }
    return false;
  }
}
